import { Avatar, Box, Collapse, IconButton, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { CheckCircle, RadioButtonUnchecked } from "@mui/icons-material";
import { SUPERSET_COLORS } from "../../utils/exerciseUtils";
import { getExercise } from "../../services/ExcersiceService";
import { imageUrl } from "../../utils/imageUrl";
import ProgramExerciseCard from "./ProgramExerciseCard";
import TraineeSwapNotice from "./TraineeSwapNotice";

const ProgramSuperSetCard = ({
    exerciseA,
    exerciseB,
    editMode,
    setEditWorkout,
    prvWorkout,
    prvWorkoutIdx,
    numberOfElements,
    blockIdx,
    weekIdx,
    openIntervals,
    setOpenIntervals,
    traineeId,
    onUnlink,
    onRemoveBoth,
}) => {
    const keyA = exerciseA.uuid || exerciseA.id || exerciseA.index;
    const isExpanded = openIntervals?.[keyA] || false;
    const setIsExpanded = (value) => {
        const newVal = typeof value === 'function' ? value(isExpanded) : value;
        setOpenIntervals(prev => ({ ...prev, [keyA]: newVal }));
    };

    const [imageA, setImageA] = useState(null);
    const [imageB, setImageB] = useState(null);
    const [nameA, setNameA] = useState(exerciseA.exerciseName || 'תרגיל א\'');
    const [nameB, setNameB] = useState(exerciseB.exerciseName || 'תרגיל ב\'');

    useEffect(() => {
        if (exerciseA.exerciseId) {
            getExercise(exerciseA.exerciseId).then(res => {
                setImageA(res.data.imageUrl || res.data.coverUrl);
                if (res.data.nameHeb) setNameA(res.data.nameHeb);
            }).catch(() => {});
        }
        if (exerciseB.exerciseId) {
            getExercise(exerciseB.exerciseId).then(res => {
                setImageB(res.data.imageUrl || res.data.coverUrl);
                if (res.data.nameHeb) setNameB(res.data.nameHeb);
            }).catch(() => {});
        }
    }, [exerciseA.exerciseId, exerciseB.exerciseId]);

    const bothDone = exerciseA.finished && exerciseB.finished;
    const sharedSets = exerciseA.intervals?.length || 0;
    const sharedRest = exerciseA.restTime || 0;

    return (
        <Paper
            elevation={1}
            sx={{
                backgroundColor: SUPERSET_COLORS.background,
                borderRight: `4px solid ${bothDone ? '#22C55E' : SUPERSET_COLORS.accent}`,
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { transform: 'translateY(-1px)' }
            }}
        >
            {/* Compact header */}
            <Box
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, cursor: 'pointer', gap: 1.5 }}
            >
                {/* Stacked images */}
                <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
                    <Avatar
                        src={imageUrl(imageA, 26)}
                        variant="rounded"
                        sx={{
                            width: 26, height: 26, borderRadius: '6px', backgroundColor: '#EDE9FE',
                            position: 'absolute', top: 0, right: 0,
                            border: `2px solid ${SUPERSET_COLORS.background}`, zIndex: 2,
                        }}
                    >
                        <FitnessCenterIcon sx={{ color: SUPERSET_COLORS.badgeA, fontSize: 12 }} />
                    </Avatar>
                    <Avatar
                        src={imageUrl(imageB, 26)}
                        variant="rounded"
                        sx={{
                            width: 26, height: 26, borderRadius: '6px', backgroundColor: '#EDE9FE',
                            position: 'absolute', bottom: 0, left: 0,
                            border: `2px solid ${SUPERSET_COLORS.background}`, zIndex: 1,
                        }}
                    >
                        <FitnessCenterIcon sx={{ color: SUPERSET_COLORS.badgeB, fontSize: 12 }} />
                    </Avatar>
                </Box>

                {/* Names */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: SUPERSET_COLORS.badgeA, fontWeight: 700, flexShrink: 0 }}>א'</Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                            {nameA}
                        </Typography>
                        {/* Icon-only: the header holds two names already. Expanding shows the label. */}
                        <TraineeSwapNotice
                            replacedByTrainee={exerciseA.replacedByTrainee}
                            originalExerciseNameHeb={exerciseA.originalExerciseNameHeb}
                            originalExerciseName={exerciseA.originalExerciseName}
                            variant="inline"
                            dense
                            iconOnly
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: SUPERSET_COLORS.badgeB, fontWeight: 700, flexShrink: 0 }}>ב'</Typography>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
                            {nameB}
                        </Typography>
                        <TraineeSwapNotice
                            replacedByTrainee={exerciseB.replacedByTrainee}
                            originalExerciseNameHeb={exerciseB.originalExerciseNameHeb}
                            originalExerciseName={exerciseB.originalExerciseName}
                            variant="inline"
                            dense
                            iconOnly
                        />
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', lineHeight: 1.2, mt: 0.25 }}>
                        סופר סט · {sharedSets} סטים · {sharedRest} שניות מנוחה
                    </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    {editMode && (
                        <>
                            <IconButton onClick={(e) => { e.stopPropagation(); onUnlink(); }} size="small"
                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: 'error.main' } }}>
                                <LinkOffIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton onClick={(e) => { e.stopPropagation(); onRemoveBoth(); }} size="small"
                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: 'error.main' } }}>
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </>
                    )}

                    <Box onClick={(e) => e.stopPropagation()}>
                        {bothDone ? <CheckCircle sx={{ color: '#22C55E', fontSize: 20 }} /> : <RadioButtonUnchecked sx={{ color: '#CBD5E1', fontSize: 20 }} />}
                    </Box>

                    <IconButton size="small" sx={{
                        color: SUPERSET_COLORS.accent, p: 0.5,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                    }}>
                        <ExpandMoreOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Expanded: both exercise cards */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: SUPERSET_COLORS.text, textAlign: 'right' }}>
                        תרגיל א'
                    </Typography>
                    <ProgramExerciseCard
                        exercise={exerciseA}
                        editMode={editMode}
                        setEditWorkout={setEditWorkout}
                        prvWorkout={prvWorkout}
                        prvWorkoutIdx={prvWorkoutIdx}
                        numberOfElements={numberOfElements}
                        blockIdx={blockIdx}
                        weekIdx={weekIdx}
                        openIntervals={openIntervals}
                        setOpenIntervals={setOpenIntervals}
                        traineeId={traineeId}
                    />

                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: SUPERSET_COLORS.text, textAlign: 'right' }}>
                        תרגיל ב'
                    </Typography>
                    <ProgramExerciseCard
                        exercise={exerciseB}
                        editMode={editMode}
                        setEditWorkout={setEditWorkout}
                        prvWorkout={prvWorkout}
                        prvWorkoutIdx={prvWorkoutIdx}
                        numberOfElements={numberOfElements}
                        blockIdx={blockIdx}
                        weekIdx={weekIdx}
                        openIntervals={openIntervals}
                        setOpenIntervals={setOpenIntervals}
                        traineeId={traineeId}
                    />
                </Box>
            </Collapse>
        </Paper>
    );
};

export default ProgramSuperSetCard;
